// ============================================================================
// Project findings — the finance view. Run B Phase 4, 2026-08-18.
//
// Run A found these and fixed none of them, deliberately. This is where they
// stop living in a report nobody reopens.
//
// READ ONLY, and that is a design decision rather than an omission: historic
// reconciliation is suspended, so a screen that could edit one of these amounts
// would be a screen that could quietly restart it. What finance can do is say
// what is correct, in words, against the record.
// ============================================================================
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { useAuth } from '../../../shared/auth';
import { Button, ErrorState, PageHeader } from '../../../shared/components';
import { errorMessage } from '../../../shared/errors';
import { recordFinanceNote, useFinanceFindings } from './use-finance-findings';

const INPUT_CLASS =
  'block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

const money = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function FinanceFindings() {
  const { employee } = useAuth();
  const { mismatches, dates, cleanup, refetch } = useFinanceFindings();
  const actorEid = employee?.employee_id ?? '';

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <PageHeader
        title="Project findings"
        meta="What Run A found and deliberately did not fix. Read only — nothing here can change an amount."
      />

      <p className="mb-5 flex items-start gap-2 rounded-lg border border-tcs-border bg-tcs-field px-3 py-2.5 text-[12.5px] text-tcs-text-secondary">
        <Lock className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Historic reconciliation is suspended, so this screen has no write path to any amount at
          all — not disabled, not permission-gated, absent. What it can do is record what is
          correct, in words, against the record, so the next person to open that card sees it.
        </span>
      </p>

      <Section
        title="Payment ≠ project pay amount"
        count={mismatches.rows.length}
        loading={mismatches.isLoading}
        error={mismatches.error}
        blurb="An automatic sub payment whose amount no longer agrees with the project. All are pending and unsynced, so nothing has left the building."
        head={['Payment', 'Project', 'Paid', 'Project says', 'Delta', 'State', '']}
      >
        {mismatches.rows.map((r) => (
          <Row
            key={r.payment_id}
            cells={[
              <span className="font-mono text-[12px]">{r.payment_id}</span>,
              <ProjectLink id={r.project_id} sub={r.facility_name} />,
              money(r.payment_amount),
              money(r.project_pay_amount),
              <span className="font-mono text-[12px]">
                {money(r.delta)}
                <div className="text-[11px] text-tcs-text-secondary">{r.direction}</div>
              </span>,
              <span className="text-[11.5px] text-tcs-text-secondary">
                {r.status}
                {r.qbo_synced === 'Y' ? ' · synced to QBO' : ' · not synced'}
                {(r.service_line_count ?? 0) > 1 ? ` · ${r.service_line_count} lines` : ''}
              </span>,
              <NoteCell
                target={{ projectId: r.project_id, label: `payment mismatch ${r.payment_id}` }}
                actorEid={actorEid}
                onSaved={refetch}
              />,
            ]}
          />
        ))}
      </Section>

      <Section
        title="Dated before we started serving the site"
        count={dates.rows.length}
        loading={dates.isLoading}
        error={dates.error}
        blurb="Backfilled projects whose earliest claimed date precedes the facility's contract_start. Measured against contract_start rather than the facility record's created_at — that one flags nine rows in ten and tells you nothing."
        head={['Project', 'Facility', 'Earliest claimed', 'Contract start', 'Days before', 'Amounts', '']}
      >
        {dates.rows.map((r) => (
          <Row
            key={r.project_id}
            cells={[
              <ProjectLink id={r.project_id} sub={r.stage} />,
              <span>{r.facility_name ?? '—'}</span>,
              <span className="font-mono text-[12px]">{r.earliest_claimed_date ?? '—'}</span>,
              <span className="font-mono text-[12px]">{r.contract_start ?? '—'}</span>,
              <span className="font-mono text-[12px]">{r.days_before_service_started ?? '—'}</span>,
              <span className="font-mono text-[12px]">
                {money(r.bill_amount)} / {money(r.pay_amount)}
              </span>,
              <NoteCell
                target={{ projectId: r.project_id, label: 'date before service started' }}
                actorEid={actorEid}
                onSaved={refetch}
              />,
            ]}
          />
        ))}
      </Section>

      <Section
        title="The cleanup list"
        count={cleanup.rows.length}
        loading={cleanup.isLoading}
        error={cleanup.error}
        blurb="Within 12 months, has a facility, a service, a date and an amount — and something about it is wrong. Rows overlap; the flags say why each one is here. Older than 12 months is archive, and incomplete is parked, per the COO's triage rules."
        head={['Project', 'Facility', 'Date', 'Bill / Pay', 'Why it is here', '']}
      >
        {cleanup.rows.map((r) => (
          <Row
            key={r.project_id}
            cells={[
              <ProjectLink id={r.project_id} sub={r.stage} />,
              <span>
                {r.facility_name ?? '—'}
                <div className="text-[11px] text-tcs-text-secondary">{r.account_name ?? ''}</div>
              </span>,
              <span className="font-mono text-[12px]">{r.best_date ?? '—'}</span>,
              <span className="font-mono text-[12px]">
                {money(r.bill_amount)} / {money(r.pay_amount)}
              </span>,
              <span className="flex flex-wrap gap-1">
                {(r.flags ?? []).map((f) => (
                  <span
                    key={f}
                    className="rounded bg-tcs-field px-1.5 py-0.5 font-mono text-[10.5px] text-tcs-text-secondary"
                  >
                    {f}
                  </span>
                ))}
              </span>,
              <NoteCell
                target={{ projectId: r.project_id, label: 'cleanup list' }}
                actorEid={actorEid}
                onSaved={refetch}
              />,
            ]}
          />
        ))}
      </Section>

      <div className="mt-8 rounded-xl border border-tcs-border bg-tcs-surface p-4 text-[12.5px] text-tcs-text-secondary">
        <p className="mb-1 text-sm font-semibold text-tcs-text">Not on any of these lists, deliberately</p>
        <p>
          170 of the 267 complete in-window projects have a sub owed with no <code>partner_payments</code>{' '}
          row at all. That is not 170 decisions — it is one systemic gap: the{' '}
          <code>jose_ap_report</code> recurring load runs 4 Jan to 15 May and then stops, so actual
          cost after May is uncomputable. It is worth more than every row above combined and it is
          already decision 6 in TCS-STATE.
        </p>
      </div>
    </div>
  );
}

function Section({
  title,
  count,
  loading,
  error,
  blurb,
  head,
  children,
}: {
  title: string;
  count: number;
  loading: boolean;
  error: string | null;
  blurb: string;
  head: string[];
  children: React.ReactNode;
}) {
  return (
    <section className="mb-7">
      <div className="mb-1 flex items-baseline gap-2">
        <h2 className="text-sm font-semibold text-tcs-text">{title}</h2>
        <span className="font-mono text-[12px] text-tcs-text-secondary">
          {loading ? '…' : count}
        </span>
      </div>
      <p className="mb-2 max-w-[85ch] text-[12px] text-tcs-text-secondary">{blurb}</p>
      {error ? (
        <ErrorState title="Couldn't load this one" body={error} />
      ) : loading ? (
        <p className="text-sm text-tcs-text-secondary">Loading…</p>
      ) : count === 0 ? (
        <p className="rounded-lg border border-tcs-border bg-tcs-surface px-3 py-2.5 text-[12.5px] text-tcs-text-secondary">
          Nothing here.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-tcs-border bg-tcs-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-tcs-field text-left text-[11px] uppercase tracking-wide text-tcs-text-muted">
                {head.map((h, i) => (
                  <th key={i} className="whitespace-nowrap px-3 py-2 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Row({ cells }: { cells: React.ReactNode[] }) {
  return (
    <tr className="border-t border-tcs-border align-top">
      {cells.map((c, i) => (
        <td key={i} className="px-3 py-2.5">
          {c}
        </td>
      ))}
    </tr>
  );
}

function ProjectLink({ id, sub }: { id: string; sub?: string | null }) {
  return (
    <span>
      <Link to={`/project-work/${id}`} className="font-mono text-[12px] text-tcs-text hover:underline">
        {id}
      </Link>
      {sub && <div className="text-[11px] text-tcs-text-secondary">{sub}</div>}
    </span>
  );
}

function NoteCell({
  target,
  actorEid,
  onSaved,
}: {
  target: { projectId: string | null; label: string };
  actorEid: string;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (done) return <span className="text-[11.5px] text-st-ok-fg">Recorded</span>;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="whitespace-nowrap rounded-md border border-tcs-border px-2.5 py-1 text-[12px] text-tcs-text-secondary hover:border-tcs-inverse/40 hover:text-tcs-text"
      >
        Say what's correct
      </button>
    );
  }

  return (
    <div className="min-w-[220px]">
      <textarea
        className={INPUT_CLASS}
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="What is actually correct here, and who says so"
      />
      <div className="mt-1.5 flex items-center gap-2">
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            try {
              if (!actorEid) throw new Error('No acting employee — sign in again.');
              await recordFinanceNote(target, note, actorEid);
              setDone(true);
              onSaved();
            } catch (e) {
              setErr(errorMessage(e, 'That note did not save.'));
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Record
        </Button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[12px] text-tcs-text-secondary hover:text-tcs-text"
        >
          Cancel
        </button>
      </div>
      {err && <p className="mt-1 text-[11.5px] text-st-danger-fg">{err}</p>}
    </div>
  );
}
